import {
  LOOKUP_LIST,
  createLabPaymentSchema,
  type CreateLabPaymentInput,
  type LabBalance,
} from "@clinic/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, type JSX } from "react";
import { Controller, useForm } from "react-hook-form";
import { revealFirstError } from "@web/shared/lib/form-errors";
import { useTranslation } from "react-i18next";
import {
  Button,
  FormField,
  Modal,
  MoneyInput,
  Select,
  Summary,
  Textarea,
  useToast,
} from "@clinic/ui";
import { Money } from "@web/shared/components/money";
import { useLookupOptions } from "@web/shared/queries/lookups";
import { usePayLab } from "@web/modules/labs/queries";
import { errorToast } from "@web/shared/lib/api-error";

export function LabPaymentModal({
  open,
  onOpenChange,
  labId,
  labName,
  balance,
  currency,
  "data-testid": testId = "lab-payment-modal",
}: {
  readonly "data-testid"?: string | undefined;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly labId: string;
  readonly labName: string;
  readonly balance: LabBalance | undefined;
  readonly currency: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const methods = useLookupOptions(LOOKUP_LIST.PAYMENT_METHOD);
  const toast = useToast();
  const pay = usePayLab();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<CreateLabPaymentInput>({
    mode: "onTouched",
    resolver: zodResolver(createLabPaymentSchema),
    defaultValues: { labId, amount: "", method: "cash", note: "" },
  });

  useEffect(() => {
    if (open) {
      reset({ labId, amount: "", method: "cash", note: "" });
    }
  }, [open, labId, reset]);

  const submit = handleSubmit(
    async (values) => {
      try {
        await pay.mutateAsync(values);
        toast.success("labs.payment.recorded");
        onOpenChange(false);
      } catch (error) {
        toast.error(...errorToast(error));
      }
    },
    () => revealFirstError(),
  );

  return (
    <Modal
      data-testid={testId}
      open={open}
      onOpenChange={onOpenChange}
      title={t("labs.payment.title", { lab: labName })}
      description={t("labs.payment.description")}
      footer={
        <>
          <Button
            variant="secondary"
            data-testid={`${testId}-cancel`}
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            isLoading={isSubmitting}
            aria-disabled={!isValid || isSubmitting || undefined}
            data-testid={`${testId}-save`}
            onClick={() => void submit()}
          >
            {t("labs.payment.submit")}
          </Button>
        </>
      }
    >
      <form
        data-testid={`${testId}-form`}
        className="flex flex-col gap-4"
        onSubmit={(event) => void submit(event)}
      >
        <Summary
          data-testid={`${testId}-current-balance`}
          rows={[
            {
              key: "balance",
              label: t("labs.payment.currentBalance"),
              value: <Money amount={balance?.balance ?? "0.00"} currency={currency} />,
              emphasis: "total",
            },
          ]}
        />

        <FormField label="labs.payment.amount" htmlFor="lab-payment-amount" error={errors.amount}>
          <MoneyInput
            id="lab-payment-amount"
            data-testid="lab-payment-field-amount"
            currency={currency}
            placeholder="0"
            hasError={Boolean(errors.amount)}
            {...register("amount")}
          />
        </FormField>

        <Controller
          control={control}
          name="method"
          render={({ field }) => (
            <FormField label="labs.payment.method" htmlFor="lab-payment-method">
              <Select
                id="lab-payment-method"
                data-testid="lab-payment-field-method"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                options={methods}
              />
            </FormField>
          )}
        />

        <FormField label="labs.payment.note" htmlFor="lab-payment-note" optional>
          <Textarea
            id="lab-payment-note"
            data-testid="lab-payment-field-note"
            rows={2}
            {...register("note")}
          />
        </FormField>
      </form>
    </Modal>
  );
}
